import React, { useState } from 'react';
import { useEffect } from "react";
import './Login.css'
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Login: React.FC = () => {
  const [loginValue, setLoginValue] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login: doLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await doLogin(loginValue, password);
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.body.classList.add("login-page"); // add class when this page mounts
    return () => {
      document.body.classList.remove("login-page"); // cleanup on unmount
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#616161',
        padding: 2,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 5,
          width: '100%',
          maxWidth: 450,
          borderRadius: 3,
          backgroundColor: '#FFFFFF',
        }}
      >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
            <Typography 
              component="h1" 
              variant="h3" 
              className="title"
              sx={{ margin: 0, fontSize: '2rem', fontWeight: 'bold' }}
            >
              Login
            </Typography>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%'}}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="login"
              label="Login"
              name="login"
              autoComplete="username"
              autoFocus
              value={loginValue}
              onChange={(e) => setLoginValue(e.target.value)}
              type="text"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&.Mui-focused fieldset': {
                    borderColor: '#CA4300',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1rem',
                  '&.Mui-focused': {
                    color: '#CA4300',
                  },
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#666' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '1rem',
                  '&.Mui-focused fieldset': {
                    borderColor: '#CA4300',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '1rem',
                  '&.Mui-focused': {
                    color: '#CA4300',
                  },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ 
                backgroundColor: '#CA4300',
                borderRadius: 2,
                fontSize: '1rem',
                fontWeight: 'bold',
                py: 1.5,
                mt: 2,
                '&:hover': {
                  backgroundColor: '#A03600'
                }
              }}
              disabled={isLoading}
            >
              {isLoading ? 'Signing In...' : 'Login'}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Link href="/register" variant="body2" sx={{ color: '#CA4300' }}>
                {"Don't have an account? Sign Up"}
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
  );
};

export default Login;
